/**
 * ReleaseReservationDialog — confirm giving a reservation's seats back
 * (BON-382, AC2).
 *
 * A reason is REQUIRED, which is why this is a dialog rather than a one-click
 * action: the acceptance criterion records who released the seats *and why*, and
 * a single button has nowhere to put the second half.
 *
 * The copy states the consequences plainly, including the one a CSR would not
 * otherwise expect — the customer is NOT emailed about this.
 */
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { releaseReservation, type ReservationRow } from '../../api/reservations';

function seatWordFor(seats: number): string {
  return seats === 1 ? 'seat' : 'seats';
}

/**
 * Escape-to-close, focus in on open, focus back on close.
 *
 * Extracted so the dialog body stays under the repo's function-length cap, and
 * because the return-focus half is the part that is easy to leave out: without
 * it a keyboard user who tabbed a long way down the reservations table is
 * dropped back at the top of the document when the dialog closes.
 */
function useDialogKeyboard(onClose: () => void, focusOnOpen: React.RefObject<HTMLElement>): void {
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    focusOnOpen.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      returnTo?.focus();
    };
    // `focusOnOpen` is a stable ref from the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref identity is stable; see above
  }, [onClose]);
}

function DialogHeader({
  reservation,
  onClose,
}: {
  reservation: ReservationRow;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-stone-200 px-6 py-4 dark:border-stone-700">
      <div>
        <h2 className="text-lg font-bold text-bark-900 dark:text-stone-100">Release held seats</h2>
        <p className="mt-0.5 text-xs text-stone-500">
          {reservation.reference} · {reservation.seats} {seatWordFor(reservation.seats)} ·{' '}
          {reservation.customerName ?? reservation.customerEmail} · {reservation.workshopName}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600 dark:hover:bg-[#252b26]"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function ReasonField({
  value,
  onChange,
  error,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}) {
  return (
    <div>
      <label
        htmlFor="release-reason"
        className="block text-sm font-semibold text-bark-900 dark:text-stone-100"
      >
        Reason for release <span className="text-red-600">*</span>
      </label>
      <textarea
        id="release-reason"
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="Why are these seats being released?"
        className="mt-1.5 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm dark:border-stone-600 dark:bg-[#1e2520] dark:text-stone-100"
      />
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function DialogFooter({
  actorName,
  seats,
  canSubmit,
  submitting,
  onClose,
}: {
  actorName: string;
  seats: number;
  canSubmit: boolean;
  submitting: boolean;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-stone-200 px-6 py-4 dark:border-stone-700">
      <p className="text-xs text-stone-500">Logged to this hold as {actorName}.</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 dark:border-stone-600 dark:text-stone-200"
        >
          Keep hold
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-lg bg-clay-600 px-4 py-2 text-sm font-semibold text-white hover:bg-clay-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Releasing…' : `Release ${seats} ${seatWordFor(seats)}`}
        </button>
      </div>
    </div>
  );
}

export function ReleaseReservationDialog({
  reservation,
  actorName,
  onClose,
  onReleased,
}: {
  reservation: ReservationRow;
  /** Shown so the CSR sees whose name goes on the audit entry. */
  actorName: string;
  onClose: () => void;
  onReleased: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The reason is required, so the textarea is where the CSR has to end up.
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  useDialogKeyboard(onClose, reasonRef);

  const canSubmit = reason.trim().length > 0 && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError('Please say why these seats are being released.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await releaseReservation(reservation.id, reason.trim());
      onReleased();
    } catch (err) {
      // A 409 here is the payment-in-flight refusal, which is a real answer
      // rather than a failure — its message explains why the seats stayed put.
      setError(err instanceof Error ? err.message : 'Could not release these seats.');
      setSubmitting(false);
    }
  };

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label="Release held seats"
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1a1f1c]"
      >
        <DialogHeader reservation={reservation} onClose={onClose} />

        <div className="space-y-4 px-6 py-5">
          <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
            {reservation.seats} {seatWordFor(reservation.seats)} go back on sale immediately and
            their booking link stops working. {reservation.customerName ?? 'The customer'} is not
            emailed.
          </div>

          <ReasonField
            value={reason}
            onChange={(v) => {
              setReason(v);
              if (error) setError(null);
            }}
            error={error}
            inputRef={reasonRef}
          />
        </div>

        <DialogFooter
          actorName={actorName}
          seats={reservation.seats}
          canSubmit={canSubmit}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </div>
  );
}
